const DAY = 86_400_000;

export const startOfDay = (d: Date | string | number) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/** Whole calendar days from today to `iso`. Negative means the past. */
export function dayOffset(iso: string, from = new Date()): number {
  return Math.round((startOfDay(iso).getTime() - startOfDay(from).getTime()) / DAY);
}

export const isToday = (iso: string | null) => iso !== null && dayOffset(iso) === 0;
export const isPast = (iso: string | null) => iso !== null && dayOffset(iso) < 0;

/** Overdue means the deadline has passed and the task is still open. */
export const isOverdue = (due: string | null, done: boolean) => !done && isPast(due);

/** A start date in the future hides the task from Today without losing the deadline. */
export const notStartedYet = (start: string | null) =>
  start !== null && dayOffset(start) > 0;

/** True when the ISO timestamp carries a meaningful clock time, not just a date. */
export function hasTime(iso: string) {
  const d = new Date(iso);
  return d.getHours() !== 0 || d.getMinutes() !== 0;
}

/*
 * Dates are formatted by hand rather than through Intl. Every label in the app
 * is a fixed, compact shape — `Fri 24 Jul`, not `Fri, Jul 24, 2026` — and the
 * locale-derived versions differ per machine, insert commas, and change width
 * unpredictably in a 12px column that is already tight.
 */

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad = (n: number) => String(n).padStart(2, '0');

/** 12-hour with a space: `2:00 PM`. */
export function formatTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours();
  return `${h % 12 || 12}:${pad(d.getMinutes())} ${h < 12 ? 'AM' : 'PM'}`;
}

/** 24-hour, zero-padded: `09:12`. Used for "completed at" and "edited at". */
export function formatClock(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `24 Jul` */
export function formatDayMonth(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

/** `Fri 24 Jul` */
export function formatFull(iso: string): string {
  const d = new Date(iso);
  return `${DAYS_SHORT[d.getDay()]} ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

/** `July 2026` */
export const formatMonthYear = (d: Date) => `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`;

/** `Wednesday` */
export const formatWeekday = (iso: string) => DAYS_LONG[new Date(iso).getDay()]!;

/** `Fri` */
export const formatWeekdayShort = (iso: string) => DAYS_SHORT[new Date(iso).getDay()]!;

/**
 * The date on a task row that stands alone — `Fri 24 Jul`, `Wed 29 Jul`.
 * Today collapses to a bare time, since the day is already on screen.
 */
export function formatDueShort(iso: string): string {
  const off = dayOffset(iso);
  if (off === 0) return hasTime(iso) ? formatTime(iso) : 'Today';
  const day = off === 1 ? 'Tomorrow' : formatFull(iso);
  // A deadline with a clock time keeps it. Showing "Fri 31 Jul" for something
  // due at 9am silently discards the half of the answer that decides the day.
  return hasTime(iso) ? `${day} ${formatTime(iso)}` : day;
}

/**
 * The narrowest honest form, for places with a column to spare rather than a
 * line: `Thu`, `12 Aug`. Used by the command palette and by secondary dates
 * inside an already-long metadata line.
 */
export function formatDueCompact(iso: string): string {
  const off = dayOffset(iso);
  if (off === 0) return hasTime(iso) ? `Today ${formatTime(iso)}` : 'Today';
  if (off > 0 && off < 7) return formatWeekdayShort(iso);
  return formatDayMonth(iso);
}

/** The "modified" stamp on a note row: a weekday this week, a date beyond it. */
export function formatModified(iso: string): string {
  const off = dayOffset(iso);
  if (off === 0) return formatClock(iso);
  if (off > -7) return formatWeekdayShort(iso);
  return formatDayMonth(iso);
}

export interface DateGroup {
  key: string;
  label: string;
  /** Secondary, lighter text beside the label — the concrete date behind the word. */
  sub: string;
}

/**
 * How Upcoming buckets a due date: named days for the near ones, a range for
 * next week, one catch-all beyond. Sorting is by `key`, which is date-ordered.
 */
export function upcomingGroup(iso: string, from = new Date()): DateGroup {
  const off = dayOffset(iso, from);
  const d = startOfDay(iso);

  if (off <= 0) return { key: '0000-overdue', label: 'Overdue', sub: '' };
  if (off === 1) return { key: isoKey(d), label: 'Tomorrow', sub: formatFull(iso) };
  if (off < 7) return { key: isoKey(d), label: formatWeekday(iso), sub: formatDayMonth(iso) };

  // The Monday–Sunday block that follows the current week.
  const nextMon = startOfDay(from);
  nextMon.setDate(nextMon.getDate() + ((8 - (nextMon.getDay() || 7)) % 7 || 7));
  const nextSun = new Date(nextMon.getTime() + 6 * DAY);
  if (d <= nextSun) {
    return {
      key: isoKey(nextMon),
      label: 'Next week',
      sub: `${nextMon.getDate()}–${formatDayMonth(nextSun.toISOString())}`,
    };
  }
  return { key: '9999-later', label: 'Later', sub: '' };
}

/** `2026-07-29` — the bucket a day is filed under. Sorts date-ordered as text. */
export const dayKey = (d: Date | string) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
};

const isoKey = dayKey;

/* ── Months ──────────────────────────────────────────────── */

/**
 * `2026-07`. The month a calendar is looking at travels in the Selection, so
 * it has to survive a round trip through a string.
 */
export const monthKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

export const parseMonthKey = (key: string): Date => {
  const [year, month] = key.split('-').map(Number);
  return new Date(year!, month! - 1, 1);
};

/** Month arithmetic on the key itself, so callers never hold a Date. */
export const shiftMonthKey = (key: string, by: number) => {
  const d = parseMonthKey(key);
  d.setMonth(d.getMonth() + by);
  return monthKey(d);
};

export const thisMonthKey = () => monthKey(new Date());

/** `July` — the year is set separately, in its own weight. */
export const monthName = (d: Date) => MONTHS_LONG[d.getMonth()]!;

export const isSameMonthKey = (key: string, d: Date) => monthKey(d) === key;

/** Today at a given clock time — what the DatePicker's quick options produce. */
export function atTime(base: Date, hours: number, minutes = 0) {
  const d = new Date(base);
  d.setHours(hours, minutes, 0, 0);
  return d;
}

export const todayISO = () => startOfDay(new Date()).toISOString();
export const tomorrowISO = () => {
  const d = startOfDay(new Date());
  d.setDate(d.getDate() + 1);
  return d.toISOString();
};
export const nextWeekISO = () => {
  const d = startOfDay(new Date());
  d.setDate(d.getDate() + ((8 - (d.getDay() || 7)) % 7 || 7));
  return d.toISOString();
};

/** The 6×7 grid a month calendar renders, padded with neighbouring months. */
export function calendarGrid(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  // Monday-first, matching the M T W T F S S header in the design.
  const lead = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - lead);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
